import React from 'react';
import { sanitizeHtml } from 'utils';
import { Checkbox, FormControlLabel, Radio, RadioGroup } from '@mui/material';

export const PrevTableElement = ({ item, readOnly }) => {
    const table = item?.properties?.table || { tableHead: [] };
    const tableHead = table.tableHead || [];
    const showHeader = table.showHeader !== false;
    const showBorders = table.showBorders !== false;
    const mergedCells = table.mergedCells || {};
    const cellTypesMap = table.cellTypes || {};
    const cellOptionsMap = table.cellOptions || {};
    const splitCellsMap = table.splitCells || {};
    const tableRows = Array.isArray(item?.value) ? item.value : [];

    const isCellHidden = (rowIndex, colIndex) => {
        for (const merge of Object.values(mergedCells)) {
            if (
                rowIndex >= merge.startRow &&
                rowIndex < merge.startRow + merge.rowSpan &&
                colIndex >= merge.startCol &&
                colIndex < merge.startCol + merge.colSpan &&
                !(rowIndex === merge.startRow && colIndex === merge.startCol)
            ) {
                return true;
            }
        }
        return false;
    };

    const getMerge = (rowIndex, colIndex) => {
        const key = `${rowIndex}_${colIndex}`;
        return mergedCells[key] || null;
    };

    const renderCellContent = (value, col, rowIndex, splitSide) => {
        const baseKey = `${rowIndex}_${col?.id}`;
        const cellKey = splitSide ? `${baseKey}_${splitSide}` : baseKey;
        const cellType = cellTypesMap[cellKey] || cellTypesMap[baseKey] || col?.cellType || 'text';
        const options = cellOptionsMap[cellKey] || cellOptionsMap[baseKey] || col?.options || [];

        if (cellType === 'checkbox') {
            return (
                <Checkbox
                    checked={!!value}
                    disabled={true}
                    size="small"
                    style={{ color: value ? '#0C66E4' : undefined, padding: 0, width: 'auto' }}
                />
            );
        }

        if (cellType === 'radio') {
            const currentId = value?.id || '';
            return (
                <div className="table-cell-radio">
                    <RadioGroup value={currentId}>
                        {options.map((opt) => (
                            <FormControlLabel
                                key={opt.id}
                                value={opt.id}
                                disabled={true}
                                control={<Radio size="small" style={{ padding: '2px 6px', width: 'auto' }} />}
                                label={opt.name}
                                style={{ marginRight: 0 }}
                                componentsProps={{ typography: { style: { fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' } } }}
                            />
                        ))}
                    </RadioGroup>
                </div>
            );
        }

        if (cellType === 'checkboxGroup') {
            const selected = Array.isArray(value) ? value : [];
            const isHorizontal = col?.optionsLayout === 'horizontal';
            return (
                <div
                    style={{ display: 'flex', flexDirection: isHorizontal ? 'row' : 'column', flexWrap: 'wrap', gap: isHorizontal ? 6 : 0 }}
                >
                    {options.map((opt) => {
                        const isChecked = selected.some((s) => (s?.id || s) === opt.id || (s?.name || s) === opt.name);
                        return (
                            <FormControlLabel
                                key={opt.id}
                                disabled={true}
                                control={
                                    <Checkbox
                                        checked={isChecked}
                                        size="small"
                                        style={{ color: isChecked ? '#0C66E4' : undefined, padding: '1px 4px', width: 'auto' }}
                                    />
                                }
                                label={opt.name}
                                style={{ marginRight: 0 }}
                                componentsProps={{ typography: { style: { fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' } } }}
                            />
                        );
                    })}
                </div>
            );
        }

        if (cellType === 'select') {
            const displayValue = typeof value === 'object' && value !== null ? value.name || '' : value || '';
            return <span style={{ wordBreak: 'break-word', fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' }}>{displayValue}</span>;
        }

        if (cellType === 'multiSelect') {
            const selected = Array.isArray(value) ? value : [];
            if (selected.length === 0) return <span style={{ fontSize: 'inherit', color: 'inherit' }}>{''}</span>;
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {selected.map((s, i) => (
                        <span key={i} style={{ wordBreak: 'break-word', fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' }}>
                            {typeof s === 'object' ? s?.name || '' : s}
                        </span>
                    ))}
                </div>
            );
        }

        // Text — render HTML entities properly
        if (typeof value === 'string' && (value.includes('<') || value.includes('&'))) {
            return <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} style={{ wordBreak: 'break-word', fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' }} />;
        }
        // Safety: if value is still an object (unknown cell type), extract .name or stringify
        if (typeof value === 'object' && value !== null) {
            return <span style={{ wordBreak: 'break-word', fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' }}>{value.name || ''}</span>;
        }
        return <span style={{ wordBreak: 'break-word', fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' }}>{value || ''}</span>;
    };

    // Check if a header cell is hidden by a header merge (startRow === -1)
    const isHeaderHidden = (colIndex) => {
        for (const merge of Object.values(mergedCells)) {
            if (merge.startRow !== -1) continue;
            if (colIndex >= merge.startCol && colIndex < merge.startCol + merge.colSpan && colIndex !== merge.startCol) {
                return true;
            }
        }
        return false;
    };

    const getHeaderMerge = (colIndex) => {
        const key = `-1_${colIndex}`;
        return mergedCells[key] || null;
    };

    return (
        <div className={`table-element-wrapper ${!showBorders ? 'no-borders' : ''}`}>
            <table className="table-element-box">
                {showHeader && (
                    <thead>
                        <tr className="table-element-head-wrapper">
                            {tableHead.map((col, hIndex) => {
                                if (isHeaderHidden(hIndex)) return null;
                                const headerMerge = getHeaderMerge(hIndex);
                                return (
                                    <th key={col.id} {...(headerMerge ? { colSpan: headerMerge.colSpan } : {})} style={{ color: '#172B4D', fontWeight: 600, fontSize: 12, ...((table.cellStyles || {})[`-1_${col.id}`] || {}) }}>
                                        <span style={{ wordBreak: 'break-word', color: 'inherit', fontWeight: 'inherit', fontSize: 'inherit', fontStyle: 'inherit', textDecoration: 'inherit' }}>
                                            {col?.name || ''}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {tableRows.map((rowData, rIndex) => (
                        <tr key={rIndex} className="table-element-body-wrapper">
                            {tableHead.map((col, cIndex) => {
                                if (isCellHidden(rIndex, cIndex)) return null;
                                const merge = getMerge(rIndex, cIndex);
                                const perCellKey = `${rIndex}_${col.id}`;
                                const isSplit = !!splitCellsMap[perCellKey];

                                if (isSplit) {
                                    const cellVal = rowData?.[col.id];
                                    const leftVal = typeof cellVal === 'object' && cellVal !== null ? cellVal.left || '' : cellVal || '';
                                    const rightVal = typeof cellVal === 'object' && cellVal !== null ? cellVal.right || '' : '';
                                    return (
                                        <td key={col.id} style={{ padding: 0, ...((table.cellStyles || {})[`${rIndex}_${col.id}`] || {}) }} className="table-split-cell">
                                            <div className="table-split-cell-inner">
                                                <div className="table-split-half" style={ ((table.cellStyles || {})[`${rIndex}_${col.id}_left`] || {}) }>{renderCellContent(leftVal, col, rIndex, 'left')}</div>
                                                <div className="table-split-divider" />
                                                <div className="table-split-half" style={ ((table.cellStyles || {})[`${rIndex}_${col.id}_right`] || {}) }>{renderCellContent(rightVal, col, rIndex, 'right')}</div>
                                            </div>
                                        </td>
                                    );
                                }

                                return (
                                    <td key={col.id} {...(merge ? { colSpan: merge.colSpan, rowSpan: merge.rowSpan } : {})} style={((table.cellStyles || {})[`${rIndex}_${col.id}`] || {})}>
                                        {renderCellContent(rowData?.[col.id], col, rIndex)}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
